import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Code,
  CopyButton,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
} from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { ApiToken, ApiTokenScope, CreatedApiToken } from "@msc/shared";

import { useApiTokens, useCreateApiToken, useRevokeApiToken } from "../../hooks/useApiTokens.js";
import { formatBerlinDate } from "../../lib/date.js";
import { ApiErrorAlert } from "../ApiErrorAlert.js";

const GUELTIGKEIT = ["30", "90", "180", "365"] as const;

/** Der Beispieleintrag für die `.mcp.json` des KI-Werkzeugs. */
export function mcpSchnipsel(origin: string, schluessel: string): string {
  return JSON.stringify(
    {
      mcpServers: {
        "service-crm": {
          type: "http",
          url: `${origin}/api/v1/mcp`,
          headers: { Authorization: `Bearer ${schluessel}` },
        },
      },
    },
    null,
    2,
  );
}

/** Einzeiler für Claude Code (`claude mcp add`, geprüft gegen `claude mcp add --help`). */
export function claudeCodeBefehl(origin: string, schluessel: string): string {
  return `claude mcp add --transport http --scope user service-crm ${origin}/api/v1/mcp --header "Authorization: Bearer ${schluessel}"`;
}

/**
 * Eintrag für Codex (`~/.codex/config.toml`). Codex liest den Schlüssel aus
 * einer Umgebungsvariable (`bearer_token_env_var`), nicht aus der Datei.
 */
export function codexKonfig(origin: string): string {
  return `[mcp_servers.service-crm]\nurl = "${origin}/api/v1/mcp"\nbearer_token_env_var = "SERVICE_CRM_TOKEN"`;
}

export function codexUmgebung(schluessel: string): string {
  return `export SERVICE_CRM_TOKEN="${schluessel}"`;
}

/** Offizielle Anleitungen der Werkzeuge (am 2026-10-13 erreichbar). */
const DOKU = {
  claudeCode: "https://code.claude.com/docs/en/mcp",
  codex: "https://learn.chatgpt.com/docs/extend/mcp?surface=cli",
  connector: "https://claude.com/docs/connectors/custom/add-unlisted",
} as const;

function KopierBlock({ wert, testId }: { wert: string; testId?: string }) {
  const { t } = useTranslation();
  return (
    <Group gap="xs" wrap="nowrap" align="flex-start">
      <Code block style={{ flex: 1, wordBreak: "break-all", whiteSpace: "pre-wrap" }} data-testid={testId}>
        {wert}
      </Code>
      <CopyButton value={wert}>
        {({ copied, copy }) => (
          <Button size="xs" variant="light" onClick={copy}>
            {copied ? t("apiTokens.copied") : t("apiTokens.copy")}
          </Button>
        )}
      </CopyButton>
    </Group>
  );
}

function Einrichtung({ schluessel }: { schluessel: string }) {
  const { t } = useTranslation();
  const origin = window.location.origin;
  return (
    <Box>
      <Text size="sm" fw={500} mb={4}>
        {t("apiTokens.setup.title")}
      </Text>
      <Tabs defaultValue="claudeCode" keepMounted={false}>
        <Tabs.List>
          <Tabs.Tab value="claudeCode">Claude Code</Tabs.Tab>
          <Tabs.Tab value="codex">Codex</Tabs.Tab>
          <Tabs.Tab value="connector">{t("apiTokens.setup.connectorTab")}</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="claudeCode" pt="sm">
          <Stack gap="xs">
            <Text size="sm">{t("apiTokens.setup.claudeCodeStep")}</Text>
            <KopierBlock wert={claudeCodeBefehl(origin, schluessel)} testId="einrichtung-claude-code" />
            <Text size="sm">{t("apiTokens.setup.claudeCodeAlt")}</Text>
            <KopierBlock wert={mcpSchnipsel(origin, schluessel)} />
            <Text size="xs" c="dimmed">{t("apiTokens.setup.claudeCodeHint")}</Text>
            <Anchor href={DOKU.claudeCode} target="_blank" rel="noreferrer" size="sm">
              {t("apiTokens.setup.docs", { name: "Claude Code" })}
            </Anchor>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="codex" pt="sm">
          <Stack gap="xs">
            <Text size="sm">{t("apiTokens.setup.codexStep1")}</Text>
            <KopierBlock wert={codexKonfig(origin)} testId="einrichtung-codex" />
            <Text size="sm">{t("apiTokens.setup.codexStep2")}</Text>
            <KopierBlock wert={codexUmgebung(schluessel)} />
            <Anchor href={DOKU.codex} target="_blank" rel="noreferrer" size="sm">
              {t("apiTokens.setup.docs", { name: "Codex" })}
            </Anchor>
          </Stack>
        </Tabs.Panel>

        <Tabs.Panel value="connector" pt="sm">
          <Stack gap="xs">
            <Text size="sm">{t("apiTokens.setup.connectorStep")}</Text>
            <KopierBlock wert={`${origin}/api/v1/mcp`} testId="einrichtung-connector" />
            <Text size="xs" c="dimmed">{t("apiTokens.setup.connectorHint")}</Text>
            <Anchor href={DOKU.connector} target="_blank" rel="noreferrer" size="sm">
              {t("apiTokens.setup.docs", { name: "Claude" })}
            </Anchor>
          </Stack>
        </Tabs.Panel>
      </Tabs>
    </Box>
  );
}

/** Claude Code ohne Schlüssel: Die Anmeldung läuft per OAuth über `/mcp` in Claude Code. */
export function claudeCodeOAuthBefehl(origin: string): string {
  return `claude mcp add --transport http --scope user service-crm ${origin}/api/v1/mcp`;
}

/**
 * Der Weg ohne Schlüssel — für die meisten der richtige: Das Programm meldet
 * sich per OAuth mit dem Microsoft-Konto an. Steht deshalb VOR dem Formular
 * und ist ohne angelegten Schlüssel sichtbar.
 */
function VerbindenOhneSchluessel() {
  const { t } = useTranslation();
  const origin = window.location.origin;
  return (
    <Alert color="blue" variant="light" title={t("apiTokens.oauth.title")}>
      <Stack gap="xs">
        <Text size="sm">{t("apiTokens.oauth.intro")}</Text>
        <Text size="sm" fw={500}>{t("apiTokens.oauth.connector")}</Text>
        <KopierBlock wert={`${origin}/api/v1/mcp`} testId="verbinden-connector" />
        <Text size="sm" fw={500}>{t("apiTokens.oauth.claudeCode")}</Text>
        <KopierBlock wert={claudeCodeOAuthBefehl(origin)} testId="verbinden-claude-code" />
        <Text size="xs" c="dimmed">{t("apiTokens.oauth.hint")}</Text>
        <Group gap="md">
          <Anchor href={DOKU.connector} target="_blank" rel="noreferrer" size="sm">
            {t("apiTokens.setup.docs", { name: "Claude" })}
          </Anchor>
          <Anchor href={DOKU.claudeCode} target="_blank" rel="noreferrer" size="sm">
            {t("apiTokens.setup.docs", { name: "Claude Code" })}
          </Anchor>
        </Group>
      </Stack>
    </Alert>
  );
}

function istAbgelaufen(token: ApiToken): boolean {
  return new Date(token.expiresAt).getTime() <= Date.now();
}

/**
 * Zugangsschlüssel verwalten. `admin` zeigt alle Schlüssel mit Spalte „Person“
 * und widerruft über den Admin-Endpunkt; angelegt wird nur für sich selbst.
 */
export function ApiTokensCard({ admin = false }: { admin?: boolean }) {
  const { t } = useTranslation();
  const { data: tokens, isLoading, error } = useApiTokens(admin);
  const create = useCreateApiToken();
  const revoke = useRevokeApiToken(admin);

  const [name, setName] = useState("");
  const [scope, setScope] = useState<ApiTokenScope>("read");
  const [tage, setTage] = useState<string>("90");
  const [created, setCreated] = useState<CreatedApiToken | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiToken | null>(null);

  const nameOk = name.trim().length >= 3;

  const submit = async () => {
    const result = await create.mutateAsync({
      name: name.trim(),
      scope,
      gueltigTage: Number(tage),
    });
    setCreated(result);
    setName("");
  };

  // Der Klartext steckt auch im Ergebnis der Mutation (React Query hält es fest);
  // beim Schließen räumt reset() ihn dort mit weg.
  const schliesseKlartext = () => {
    setCreated(null);
    create.reset();
  };

  const status = (tk: ApiToken) =>
    tk.revokedAt ? (
      <Badge color="gray">{t("apiTokens.revoked")}</Badge>
    ) : istAbgelaufen(tk) ? (
      <Badge color="orange">{t("apiTokens.expired")}</Badge>
    ) : null;

  return (
    <Stack gap="md">
      {!admin ? <VerbindenOhneSchluessel /> : null}
      {!admin ? (
        <Stack gap="xs">
          <Text size="sm" fw={500}>
            {t("apiTokens.withKeyTitle")}
          </Text>
          <Text size="sm" c="dimmed">
            {t("apiTokens.intro")}
          </Text>
          <TextInput
            label={t("apiTokens.name")}
            placeholder={t("apiTokens.namePlaceholder")}
            value={name}
            onChange={(e) => setName(e.currentTarget.value)}
            maxLength={80}
          />
          <Box>
            <Text size="sm" fw={500}>
              {t("apiTokens.scope")}
            </Text>
            <SegmentedControl
              value={scope}
              onChange={(v) => setScope(v as ApiTokenScope)}
              data={[
                { value: "read", label: t("apiTokens.scopeRead") },
                { value: "write", label: t("apiTokens.scopeWrite") },
              ]}
            />
          </Box>
          <Box>
            <Text size="sm" fw={500}>
              {t("apiTokens.validity")}
            </Text>
            <SegmentedControl
              value={tage}
              onChange={setTage}
              data={GUELTIGKEIT.map((d) => ({
                value: d,
                label: t("apiTokens.days", { count: Number(d) }),
              }))}
            />
          </Box>
          {create.error ? <ApiErrorAlert error={create.error} /> : null}
          <Group>
            <Button onClick={() => void submit()} disabled={!nameOk} loading={create.isPending}>
              {t("apiTokens.create")}
            </Button>
          </Group>
        </Stack>
      ) : null}

      {error ? <ApiErrorAlert error={error} /> : null}

      <Box style={{ overflowX: "auto" }}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              {admin ? <Table.Th>{t("apiTokens.person")}</Table.Th> : null}
              <Table.Th>{t("apiTokens.name")}</Table.Th>
              <Table.Th>{t("apiTokens.scope")}</Table.Th>
              <Table.Th>{t("apiTokens.prefix")}</Table.Th>
              <Table.Th>{t("apiTokens.createdAt")}</Table.Th>
              <Table.Th>{t("apiTokens.expiresAt")}</Table.Th>
              <Table.Th>{t("apiTokens.lastUsedAt")}</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(tokens ?? []).map((tk) => (
              <Table.Tr key={tk.id}>
                {admin ? <Table.Td>{tk.employee?.fullName ?? "–"}</Table.Td> : null}
                <Table.Td>
                  <Group gap="xs">
                    <Text size="sm">
                      {tk.oauthGrant ? t("apiTokens.oauthGrant", { name: tk.name }) : tk.name}
                    </Text>
                    {tk.kind !== "pat" && !tk.oauthGrant ? (
                      <Badge variant="light">{t(`apiTokens.kind.${tk.kind}`)}</Badge>
                    ) : null}
                    {status(tk)}
                  </Group>
                </Table.Td>
                <Table.Td>
                  {tk.scope === "read" ? t("apiTokens.scopeRead") : t("apiTokens.scopeWrite")}
                </Table.Td>
                <Table.Td>
                  <Code>{tk.prefix}…</Code>
                </Table.Td>
                <Table.Td>{formatBerlinDate(tk.createdAt)}</Table.Td>
                <Table.Td>{formatBerlinDate(tk.expiresAt)}</Table.Td>
                <Table.Td>
                  {tk.lastUsedAt ? formatBerlinDate(tk.lastUsedAt) : t("apiTokens.neverUsed")}
                </Table.Td>
                <Table.Td>
                  {!tk.revokedAt && !istAbgelaufen(tk) ? (
                    <Button
                      size="compact-xs"
                      color="red"
                      variant="light"
                      onClick={() => setRevokeTarget(tk)}
                    >
                      {t("apiTokens.revoke")}
                    </Button>
                  ) : null}
                </Table.Td>
              </Table.Tr>
            ))}
            {!isLoading && (tokens ?? []).length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={admin ? 8 : 7}>
                  <Text size="sm" c="dimmed">
                    {t("apiTokens.empty")}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : null}
          </Table.Tbody>
        </Table>
      </Box>

      <Modal
        opened={revokeTarget !== null}
        onClose={() => setRevokeTarget(null)}
        title={t("apiTokens.revokeTitle")}
      >
        <Stack gap="md">
          <Text size="sm">
            {t(revokeTarget?.oauthGrant ? "apiTokens.revokeGrantConfirm" : "apiTokens.revokeConfirm", {
              name: revokeTarget?.name ?? "",
            })}
          </Text>
          {revoke.error ? <ApiErrorAlert error={revoke.error} /> : null}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setRevokeTarget(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              color="red"
              loading={revoke.isPending}
              onClick={async () => {
                if (!revokeTarget) return;
                await revoke.mutateAsync(revokeTarget.id);
                setRevokeTarget(null);
              }}
            >
              {t("apiTokens.revokeConfirmButton")}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={created !== null}
        onClose={schliesseKlartext}
        title={t("apiTokens.createdTitle")}
        size="lg"
        closeOnClickOutside={false}
      >
        {created ? (
          <Stack gap="md">
            <Alert color="orange" variant="light">
              {t("apiTokens.onlyNow")}
            </Alert>
            <KopierBlock wert={created.klartext} testId="api-token-klartext" />
            <Einrichtung schluessel={created.klartext} />
            <Alert color="blue" variant="light">
              {t("apiTokens.privacyNote")}
            </Alert>
            <Group justify="flex-end">
              <Button onClick={schliesseKlartext}>{t("common.close")}</Button>
            </Group>
          </Stack>
        ) : null}
      </Modal>
    </Stack>
  );
}
